import PropTypes from 'prop-types'
import { GoogleMap, Marker, OverlayView, useLoadScript } from '@react-google-maps/api'
import { useMemo } from 'react'
import { getCountryInfo } from '../../../shared/utils/countryUtils'

const countryCoordinates = {
  'United Arab Emirates': { lat: 23.4241, lng: 53.8478 },
  Russia: { lat: 61.524, lng: 105.3188 },
  China: { lat: 35.8617, lng: 104.1954 },
  'United States': { lat: 39.8283, lng: -98.5795 },
  Canada: { lat: 56.1304, lng: -106.3468 },
  Brazil: { lat: -14.235, lng: -51.9253 },
  'United Kingdom': { lat: 54.7584, lng: -2.6953 },
  Nigeria: { lat: 9.082, lng: 8.6753 },
  Kenya: { lat: 0.1769, lng: 37.9062 },
  'South Africa': { lat: -30.5595, lng: 22.9375 },
  Turkey: { lat: 38.9637, lng: 35.2433 },
  Australia: { lat: -25.2744, lng: 133.7751 },
}

const Map = ({ center, zoom = 3, markers = [], height = '400px' }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  })

  const containerStyle = useMemo(() => ({
    width: '100%',
    height: height,
  }), [height])

  const processedMarkers = useMemo(() => {
    return markers.map(marker => {
      // Get country info using our dynamic utility
      const countryInfo = getCountryInfo(marker.title)
      const countryCode = marker.countryCode || countryInfo?.code || marker.title?.slice(0, 2).toLowerCase()
      const countryFlag = marker.flag || countryInfo?.flag

      return {
        ...marker,
        position: marker.position?.lat && marker.position?.lng
          ? marker.position
          : countryCoordinates[marker.title] || center,
        countryCode: countryCode,
        flag: countryFlag
      }
    })
  }, [markers, center])

  if (loadError) return <div>Error loading map. Check your API key.</div>
  if (!isLoaded) return <div>Loading map...</div>

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={zoom}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        minZoom: 1.5,
        maxZoom: 18,
        styles: [
          // General land
          {
            featureType: 'landscape',
            elementType: 'geometry',
            stylers: [{ color: '#cdced3' }], // light beige
          },
          // Water
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#efe9e1' }], // light gray-blue
          },
          // Administrative country boundaries
          {
            featureType: 'administrative.country',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#cdced3' }], // light border
          },
          {
            featureType: 'administrative.country',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'transit',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      }}
    >
      {processedMarkers.map((marker, index) => {
        const count = parseInt(marker.label) || 0
        const countryCode = marker.countryCode
        const countryFlag = marker.flag

        return (
          <div key={marker.id || index}>
            {/* Invisible marker to anchor overlay */}
            <Marker position={marker.position} opacity={0} />

            {/* Custom tooltip */}
            <OverlayView
              position={marker.position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(width, height) => ({
                x: -width / 2,
                y: -height - 10,
              })}
            >
              <div style={{ position: 'relative', display: 'inline-block' }}>
                {/* Tooltip box */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    padding: '2px 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    fontFamily: 'Arial, sans-serif',
                    whiteSpace: 'nowrap',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#000',
                  }}
                >
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={`https://flagcdn.com/16x12/${countryCode?.toLowerCase()}.png`}
                      alt={marker.title}
                      style={{
                        borderRadius: '2px',
                        border: '1px solid #ddd',
                        width: '16px',
                        height: '12px',
                        display: 'inline-block'
                      }}
                      onError={(e) => {
                        // Hide image and show emoji fallback
                        e.target.style.display = 'none'
                        if (e.target.nextElementSibling && countryFlag) {
                          e.target.nextElementSibling.style.display = 'inline-block'
                        }
                      }}
                    />
                    {countryFlag && (
                      <span
                        style={{
                          fontSize: '16px',
                          display: 'none',
                          lineHeight: '12px'
                        }}
                      >
                        {countryFlag}
                      </span>
                    )}
                  </div>
                  {count}
                </div>

                {/* Red dot pointer */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-9px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '8px',
                    height: '8px',
                    backgroundColor: 'red',
                    borderRadius: '50%',
                    border: '1px solid white',
                  }}
                />
              </div>
            </OverlayView>
          </div>
        )
      })}
    </GoogleMap>
  )
}

Map.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  zoom: PropTypes.number,
  height: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      position: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      title: PropTypes.string,
      label: PropTypes.string,
      countryCode: PropTypes.string,
    })
  ),
}

Map.defaultProps = {
  zoom: 3,
  height: '400px',
  markers: [],
}

export default Map